// -------------------------------------------------------------------------------------------------
// Import
// -------------------------------------------------------------------------------------------------

import {
  debounce as _debounce,
  throttle as _throttle,
} from 'lodash-es';
import {getHash, LAYOUT_CHANGE_EVENT, tryExecution} from '../_util';
import themeCommon from './_common';

// -------------------------------------------------------------------------------------------------
// Variables
// -------------------------------------------------------------------------------------------------

const baseClass = 'hr-list';
const hrLists = [];
const itemNegativeMarginPixels = 1;
let updateThrottle = null;
let updateDebounce = null;

// -------------------------------------------------------------------------------------------------
// Classes
// -------------------------------------------------------------------------------------------------

class HrListSplit {
  /**
   * @param {Element} el
   */
  constructor(el) {
    const [ firstItems, ] = this.getListItemHeights(el, null);
    const [ firstList, secondList, splitContainer ] = this.wrapElementInContainer(el);

    this.firstList = firstList;
    this.secondList = secondList;
    this.splitContainer = splitContainer;

    this.moveItemsBetweenLists(
      firstList,
      secondList,
      firstItems,
      []
    );

    this.lastListHeight = this.getTotalListHeight(firstItems, []);
    this.splitContainer.classList.add('hr-list-container--loaded');

    el.replaceWith(this.splitContainer);
  }

  wrapElementInContainer(element) {
    const disallowedClasses = [
      'hr-list--split',
      'hr-list--limited-height',
    ];

    const firstList = /** @type {Element} */ element.cloneNode(true);

    const classes = element.classList;
    const cleanedClasses = Array.from(classes).filter((x) => disallowedClasses.includes(x) === false);

    const secondList = document.createElement('ul');
    secondList.classList.add('hr-list');
    const splitContainer = document.createElement('div');
    splitContainer.classList.add('hr-list-container');

    firstList.className = "";

    firstList.classList.add(...cleanedClasses);
    secondList.classList.add(...cleanedClasses);

    if (classes.contains('hr-list--limited-height')) {
      splitContainer.classList.add('hr-list-container--limited-height');
    }

    splitContainer.appendChild(firstList);
    splitContainer.appendChild(secondList);

    return [ firstList, secondList, splitContainer ];
  }

  /**
   *
   * @param {number[]|null} firstItems
   * @param {number[]|null} secondItems
   * @returns number
   */
  getTotalListHeight(firstItems, secondItems) {
    return this.getItemsTotalHeight(firstItems) + this.getItemsTotalHeight(secondItems);
  }

  /**
   * @param {number[]|null} items
   * @returns number
   */
  getItemsTotalHeight(items) {
    if (items == null) {
      return 0;
    }

    return items.reduce((current, height) => current + height, 0);
  }

  /**
   * @param {Element|null} firstList
   * @param {Element|null} secondList
   * @returns {[number[], number[]]}
   */
  getListItemHeights(firstList, secondList) {
    const firstItems = firstList ? Array.from(firstList.querySelectorAll(':scope > li')) : [];
    const secondItems = secondList ? Array.from(secondList.querySelectorAll(':scope > li')) : [];

    return [
      firstItems.map(item => item.offsetHeight - itemNegativeMarginPixels),
      secondItems.map(item => item.offsetHeight - itemNegativeMarginPixels),
    ];
  }

  /**
   *
   * @param {Element} firstList
   * @param {Element} secondList
   * @param {number[]} firstItems
   * @param {number[]} secondItems
   */
  moveItemsBetweenLists(firstList, secondList, firstItems, secondItems) {
    const firstItemsCopy = [...firstItems];
    const secondItemsCopy = [...secondItems];

    let firstListTotalHeight = this.getItemsTotalHeight(firstItems);
    let secondListTotalHeight = this.getItemsTotalHeight(secondItems);
    const firstListTargetHeight = Math.ceil((firstListTotalHeight + secondListTotalHeight)  / 2);

    if (firstListTotalHeight === secondListTotalHeight) {
      return;
    }

    let leftNeedsFill = firstListTotalHeight < firstListTargetHeight;

    let getNextHeight = () => firstItemsCopy.pop();
    let moveChild = (scrollHeight) => {
      secondList.prepend(firstList.lastElementChild);

      secondListTotalHeight += scrollHeight;
      firstListTotalHeight -= scrollHeight;
    };
    let canMove = (scrollHeight) => {
      const leftHeight = firstListTotalHeight - scrollHeight;
      const rightHeight = secondListTotalHeight + scrollHeight;

      return leftHeight >= rightHeight;
    };

    if (leftNeedsFill) {
      getNextHeight = () => secondItemsCopy.shift();
      moveChild = (scrollHeight) => {

        firstList.append(secondList.firstElementChild);

        firstListTotalHeight += scrollHeight;
        secondListTotalHeight -= scrollHeight;
      };
      canMove = () => {
        return firstListTotalHeight < secondListTotalHeight;
      };
    }

    let scrollHeight = getNextHeight();
    while (scrollHeight != null && canMove(scrollHeight)) {
      moveChild(scrollHeight);

      scrollHeight = getNextHeight();
    }
  }

  update() {
    const [ firstItems, secondItems ] = this.getListItemHeights(this.firstList, this.secondList);
    const newHeight = this.getTotalListHeight(firstItems, secondItems);
    if (Math.abs(this.lastListHeight - newHeight) <= 5) { // Some tolerance to avoid overly accurate rebuild
      return;
    }

    const firstList = /** @type {Element} */ this.firstList.cloneNode(true);
    const secondList = /** @type {Element} */ this.secondList.cloneNode(true);

    this.moveItemsBetweenLists(
      firstList,
      secondList,
      firstItems,
      secondItems
    );

    this.firstList.replaceWith(firstList);
    this.secondList.replaceWith(secondList);

    this.firstList = firstList;
    this.secondList = secondList;

    this.lastListHeight = this.getTotalListHeight(firstItems, secondItems);
  }
}

function onHashChange() {
  const hash = getHash();
  if (hash == null || hash === '') {
    return;
  }

  const targetRow = document.querySelector(`.${baseClass} #${CSS.escape(hash)}`);
  let targetDetails = null;
  if (targetRow) {
    targetDetails = targetRow.querySelector('details');
  }

  const openDetails = document.querySelectorAll(`.${baseClass} details[open]`);
  for (let i = 0; i < openDetails.length; i++) {
    openDetails[i].removeAttribute('open');
  }

  if (targetDetails) {
    targetDetails.setAttribute('open', 'true');
  }

  if (targetRow) {
    themeCommon.smoothScrollTo(targetRow, 750);
  }
}


function updateAllLists() {
  for (const hrList of hrLists) {
    tryExecution(() => {
      hrList.update();
    });
  }
}

// -------------------------------------------------------------------------------------------------
// Methods
// -------------------------------------------------------------------------------------------------

function init() {
  updateThrottle = _throttle(updateAllLists, 50);
  updateDebounce = _debounce(updateAllLists, 300);

  const splittedLists = document.querySelectorAll(`.${baseClass}.${baseClass}--split`);
  for (let i = 0; i < splittedLists.length; i++) {
    tryExecution(() => {
      hrLists.push(new HrListSplit(splittedLists[i]));
    });
  }

  window.addEventListener('resize', updateDebounce);
  window.addEventListener('hashchange', onHashChange);

  onHashChange();

  document.addEventListener(LAYOUT_CHANGE_EVENT, () => {
    updateThrottle();
  }, { passive: true });
}

// -------------------------------------------------------------------------------------------------
// Export
// -------------------------------------------------------------------------------------------------

export default {
  init,
};
